/**
 * Shared Three.js geometries and materials for the 3D canvas.
 * Reusing these cuts draw-call material switches and GC from per-mesh JSX allocs.
 */
import * as THREE from 'three';

const UNIT_BOX = new THREE.BoxGeometry(1, 1, 1);
const UNIT_CYLINDER = new THREE.CylinderGeometry(1, 1, 1, 12);
const UNIT_CYLINDER_24 = new THREE.CylinderGeometry(1, 1, 1, 24);

const geometryCache = new Map<string, THREE.BufferGeometry>();
const materialCache = new Map<string, THREE.MeshStandardMaterial>();
const physicalMaterialCache = new Map<string, THREE.MeshPhysicalMaterial>();

export function getUnitBoxGeometry(): THREE.BoxGeometry {
  return UNIT_BOX;
}

export function getUnitCylinderGeometry(segments = 12): THREE.CylinderGeometry {
  return segments >= 24 ? UNIT_CYLINDER_24 : UNIT_CYLINDER;
}

export function getBoxGeometry(sx: number, sy: number, sz: number): THREE.BoxGeometry {
  const key = `box:${sx.toFixed(4)}:${sy.toFixed(4)}:${sz.toFixed(4)}`;
  let geo = geometryCache.get(key) as THREE.BoxGeometry | undefined;
  if (!geo) {
    geo = new THREE.BoxGeometry(sx, sy, sz);
    geometryCache.set(key, geo);
  }
  return geo;
}

export type StandardMatKey = {
  color: string;
  metalness?: number;
  roughness?: number;
  envMapIntensity?: number;
  emissive?: string;
  emissiveIntensity?: number;
  opacity?: number;
  transparent?: boolean;
  depthWrite?: boolean;
  toneMapped?: boolean;
  side?: THREE.Side;
  map?: THREE.Texture;
  roughnessMap?: THREE.Texture;
};

function matCacheKey(spec: StandardMatKey): string {
  return [
    spec.color,
    spec.metalness ?? 0,
    spec.roughness ?? 1,
    spec.envMapIntensity ?? 1,
    spec.emissive ?? '#000000',
    spec.emissiveIntensity ?? 0,
    spec.opacity ?? 1,
    spec.transparent ? 1 : 0,
    spec.depthWrite === false ? 0 : 1,
    spec.toneMapped === false ? 0 : 1,
    spec.side ?? THREE.FrontSide,
    spec.map?.uuid ?? '',
    spec.roughnessMap?.uuid ?? '',
  ].join('|');
}

/** Shared MeshStandardMaterial — do not mutate returned instances per-mesh. */
export function getSharedStandardMaterial(spec: StandardMatKey): THREE.MeshStandardMaterial {
  const key = matCacheKey(spec);
  let mat = materialCache.get(key);
  if (!mat) {
    const opacity = spec.opacity ?? 1;
    const params: THREE.MeshStandardMaterialParameters = {
      color: spec.color,
      metalness: spec.metalness ?? 0,
      roughness: spec.roughness ?? 1,
      envMapIntensity: spec.envMapIntensity ?? 1,
      emissive: spec.emissive ?? '#000000',
      emissiveIntensity: spec.emissiveIntensity ?? 0,
      transparent: spec.transparent ?? opacity < 1,
      opacity,
      depthWrite: spec.depthWrite ?? true,
      toneMapped: spec.toneMapped ?? true,
      side: spec.side ?? THREE.FrontSide,
    };
    if (spec.map) params.map = spec.map;
    if (spec.roughnessMap) params.roughnessMap = spec.roughnessMap;
    mat = new THREE.MeshStandardMaterial(params);
    materialCache.set(key, mat);
  }
  return mat;
}

/** Rail palette materials used by DualRailTrack3D / RailInstancedLayer3D. */
export const RAIL_POOL = {
  aluminum: {
    metalness: 0.45,
    roughness: 0.25,
    envMapIntensity: 0.95,
  },
  aluminumSoft: {
    metalness: 0.35,
    roughness: 0.35,
    envMapIntensity: 0.85,
  },
} as const;

export function getRailBarMaterial(
  color: string,
  opts?: {
    metalness?: number;
    roughness?: number;
    envMapIntensity?: number;
    emissive?: string;
    emissiveIntensity?: number;
    opacity?: number;
  }
): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color,
    metalness: opts?.metalness ?? RAIL_POOL.aluminum.metalness,
    roughness: opts?.roughness ?? RAIL_POOL.aluminum.roughness,
    envMapIntensity: opts?.envMapIntensity ?? RAIL_POOL.aluminum.envMapIntensity,
    emissive: opts?.emissive,
    emissiveIntensity: opts?.emissiveIntensity,
    opacity: opts?.opacity ?? 1,
  });
}

export function getRailWhiteMaterial(opacity = 1): THREE.MeshStandardMaterial {
  return getRailBarMaterial('#FFFFFF', {
    ...RAIL_POOL.aluminum,
    opacity,
  });
}


/** Default FOUP shell materials (opaque, no custom tint). */
export function getDefaultFoupMaterials(opacity = 1): {
  black: THREE.MeshStandardMaterial;
  blackSoft: THREE.MeshStandardMaterial;
  doorGlass: THREE.MeshStandardMaterial;
  baseGlass: THREE.MeshStandardMaterial;
  sideGlass: THREE.MeshStandardMaterial;
  wafer: THREE.MeshStandardMaterial;
  latch: THREE.MeshStandardMaterial;
  interior: THREE.MeshStandardMaterial;
} {
  const a = opacity < 1 ? opacity : 1;
  return {
    black: getSharedStandardMaterial({
      color: '#0B0D10',
      metalness: 0.2,
      roughness: 0.48,
      opacity: a,
    }),
    blackSoft: getSharedStandardMaterial({
      color: '#1A1E23',
      metalness: 0.18,
      roughness: 0.52,
      opacity: a,
    }),
    doorGlass: getSharedStandardMaterial({
      color: '#3C444C',
      metalness: 0.35,
      roughness: 0.06,
      transparent: true,
      opacity: Math.min(0.42, a * 0.55),
      depthWrite: false,
    }),
    baseGlass: getSharedStandardMaterial({
      color: '#2B3238',
      metalness: 0.4,
      roughness: 0.12,
      transparent: true,
      opacity: Math.min(0.52, a * 0.7),
    }),
    sideGlass: getSharedStandardMaterial({
      color: '#252C32',
      metalness: 0.35,
      roughness: 0.1,
      transparent: true,
      opacity: Math.min(0.48, a * 0.65),
    }),
    wafer: getSharedStandardMaterial({
      color: '#1A1E23',
      metalness: 0.22,
      roughness: 0.48,
      opacity: a,
    }),
    latch: getSharedStandardMaterial({
      color: '#CC1220',
      metalness: 0.6,
      roughness: 0.34,
      opacity: a,
    }),
    interior: getSharedStandardMaterial({
      color: '#050607',
      metalness: 0.2,
      roughness: 0.7,
      opacity: a,
    }),
  };
}

export function getGantryMaterial(
  color: string,
  metalness: number,
  roughness: number,
  opacity = 1
): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({ color, metalness, roughness, opacity });
}

export type PhysicalMatKey = {
  color: string;
  metalness?: number;
  roughness?: number;
  envMapIntensity?: number;
  emissive?: string;
  emissiveIntensity?: number;
  opacity?: number;
  transparent?: boolean;
  depthWrite?: boolean;
  toneMapped?: boolean;
  side?: THREE.Side;
  transmission?: number;
  thickness?: number;
  ior?: number;
  specularIntensity?: number;
  clearcoat?: number;
  clearcoatRoughness?: number;
  attenuationColor?: string;
  attenuationDistance?: number;
};

function physicalMatCacheKey(spec: PhysicalMatKey): string {
  return [
    spec.color,
    spec.metalness ?? 0,
    spec.roughness ?? 1,
    spec.envMapIntensity ?? 1,
    spec.emissive ?? '#000000',
    spec.emissiveIntensity ?? 0,
    spec.opacity ?? 1,
    spec.transparent ? 1 : 0,
    spec.depthWrite === false ? 0 : 1,
    spec.toneMapped === false ? 0 : 1,
    spec.side ?? THREE.FrontSide,
    spec.transmission ?? 0,
    spec.thickness ?? 0,
    spec.ior ?? 1.5,
    spec.specularIntensity ?? 1,
    spec.clearcoat ?? 0,
    spec.clearcoatRoughness ?? 0,
    spec.attenuationColor ?? '',
    spec.attenuationDistance ?? 0,
  ].join('|');
}

/** Shared MeshPhysicalMaterial — caches high-end glass, clearcoats, and transmission surfaces. */
export function getSharedPhysicalMaterial(spec: PhysicalMatKey): THREE.MeshPhysicalMaterial {
  const key = physicalMatCacheKey(spec);
  let mat = physicalMaterialCache.get(key);
  if (!mat) {
    const opacity = spec.opacity ?? 1;
    const params: THREE.MeshPhysicalMaterialParameters = {
      color: spec.color,
      metalness: spec.metalness ?? 0,
      roughness: spec.roughness ?? 1,
      envMapIntensity: spec.envMapIntensity ?? 1,
      emissive: spec.emissive ?? '#000000',
      emissiveIntensity: spec.emissiveIntensity ?? 0,
      transparent: spec.transparent ?? (opacity < 1 || (spec.transmission ?? 0) > 0),
      opacity,
      depthWrite: spec.depthWrite ?? (spec.transmission ?? 0) === 0,
      toneMapped: spec.toneMapped ?? true,
      side: spec.side ?? THREE.FrontSide,
      transmission: spec.transmission ?? 0,
      thickness: spec.thickness ?? 0,
      ior: spec.ior ?? 1.5,
      specularIntensity: spec.specularIntensity ?? 1,
      clearcoat: spec.clearcoat ?? 0,
      clearcoatRoughness: spec.clearcoatRoughness ?? 0,
    };
    if (spec.attenuationColor) {
      params.attenuationColor = new THREE.Color(spec.attenuationColor);
    }
    if (spec.attenuationDistance !== undefined) {
      params.attenuationDistance = spec.attenuationDistance;
    }
    mat = new THREE.MeshPhysicalMaterial(params);
    physicalMaterialCache.set(key, mat);
  }
  return mat;
}

/** Teal-tinted cabinet window glass for instanced equipment shells. */
export function getSharedCabinetGlassMaterial(): THREE.MeshPhysicalMaterial {
  return getSharedPhysicalMaterial({
    color: '#8ecfdc',
    metalness: 0.05,
    roughness: 0.08,
    transmission: 0.42,
    thickness: 0.6,
    ior: 1.45,
    transparent: true,
    opacity: 0.78,
    depthWrite: false,
    side: THREE.DoubleSide,
    envMapIntensity: 1.2,
    specularIntensity: 0.85,
  });
}

/** Premium physically plausible cleanroom inspection glass with reflections and IOR. */
export function getCleanroomGlassMaterial(tint = '#BAE6FD', opacity = 0.85): THREE.MeshPhysicalMaterial {
  return getSharedPhysicalMaterial({
    color: tint,
    metalness: 0.04,
    roughness: 0.06,
    transmission: 0.85,
    thickness: 0.8,
    ior: 1.52,
    transparent: true,
    opacity,
    depthWrite: false,
    side: THREE.DoubleSide,
    envMapIntensity: 1.45,
    specularIntensity: 0.95,
  });
}

/** High-grade industrial powder-coated white with clearcoat highlight. */
export function getIndustrialWhiteMaterial(opacity = 1): THREE.MeshPhysicalMaterial {
  return getSharedPhysicalMaterial({
    color: '#FFFFFF',
    metalness: 0.06,
    roughness: 0.16,
    clearcoat: 0.45,
    clearcoatRoughness: 0.12,
    envMapIntensity: 1.25,
    opacity,
    transparent: opacity < 1,
  });
}

/** High-grade industrial dark anthracite chassis with subtle metallic sheen. */
export function getIndustrialDarkChassisMaterial(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: '#181E26',
    metalness: 0.75,
    roughness: 0.26,
    envMapIntensity: 1.35,
    opacity,
    transparent: opacity < 1,
  });
}

/** Satin aerospace-grade brushed aluminum for structural gantries and rails. */
export function getBrushedAluminumMaterial(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: '#CBD5E1',
    metalness: 0.90,
    roughness: 0.20,
    envMapIntensity: 1.6,
    opacity,
    transparent: opacity < 1,
  });
}

/** Mirror-polished stainless steel and chrome for latches, fasteners, and wheel treads. */
export function getPolishedChromeMaterial(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: '#F8FAFC',
    metalness: 0.98,
    roughness: 0.06,
    envMapIntensity: 2.2,
    opacity,
    transparent: opacity < 1,
  });
}

/** Polycarbonate semiconductor wafer carrier shell material. */
export function getPolycarbonateFoupMaterial(color = '#0F2B5C', opacity = 1): THREE.MeshPhysicalMaterial {
  return getSharedPhysicalMaterial({
    color,
    metalness: 0.35,
    roughness: 0.14,
    transmission: 0.48,
    thickness: 0.45,
    ior: 1.58,
    clearcoat: 0.65,
    clearcoatRoughness: 0.1,
    envMapIntensity: 1.45,
    opacity,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

/** Crisp status LED with calibrated emissive response for selective bloom. */
export function getStatusLedMaterial(color: string, intensity = 1.6): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: intensity,
    roughness: 0.2,
    metalness: 0.1,
    toneMapped: false,
  });
}

